import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import AdminModal from '../../components/AdminModal';
import { padDistrictNumber } from '../../../utils/districtDisplay';

interface ReconcileMatch {
  candidate_id: string;
  office_sought: string;
  district: string | null;
  district_display: string | null;
  party_affiliation: string | null;
  full_name: string;
  legislator_id: number;
  chamber: string | null;
  legislator_district: number | null;
  session_year: string | null;
}

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ??
  error?.response?.data?.message ??
  error?.response?.data?.errors?.[0]?.msg ??
  fallback;

const inputClasses =
  'w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';

/**
 * Election-results reconciliation (PEOPLE_TOOL_PLAN.md §3.3 — P3). Finds
 * active legislative candidates whose person appears in the new session
 * roster; confirming applies the role transition — the candidacy closes as
 * elected and an officeholder term opens (prior seat holder ended).
 */
const ReconcileModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [electionYear, setElectionYear] = useState('');
  const [sessionYear, setSessionYear] = useState('');
  const [matches, setMatches] = useState<Array<ReconcileMatch & { confirmed: boolean }> | null>(null);

  // Election years come from configured cycles (ELECTIONS_TOOL_PLAN.md E2) —
  // reconciling a year without a cycle can't match anything anyway.
  const cycles = useQuery({
    queryKey: ['admin-elections', 'cycles', 'CO'],
    queryFn: async () => {
      const response = await api.get('/election-calendars', { params: { state: 'CO' } });
      return (response.data.data ?? []) as Array<{ election_year: number; election_type: string }>;
    },
    staleTime: 5 * 60 * 1000,
    enabled: isOpen,
  });
  const cycleYears = [...new Set(
    (cycles.data ?? [])
      .filter((cycle) => cycle.election_type === 'regular')
      .map((cycle) => cycle.election_year)
  )].sort((a, b) => b - a);

  const reset = () => {
    setMatches(null);
    setElectionYear('');
    setSessionYear('');
  };

  const findMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/admin/candidates/reconcile', {
        election_year: parseInt(electionYear, 10),
        session_year: parseInt(sessionYear, 10),
      });
      return (response.data.data ?? []) as ReconcileMatch[];
    },
    onSuccess: (rows) => setMatches(rows.map((row) => ({ ...row, confirmed: true }))),
    onError: (error: any) => toast.error(apiError(error, 'Failed to find matches')),
  });

  const confirmMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/candidates/mark-elected', {
        matches: (matches ?? [])
          .filter((match) => match.confirmed)
          .map((match) => ({ candidateId: match.candidate_id, legislatorId: match.legislator_id })),
      }),
    onSuccess: (response) => {
      toast.success(response.data.message ?? 'Candidates marked elected');
      queryClient.invalidateQueries({ queryKey: ['admin-list'] });
      reset();
      onClose();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to mark candidates elected')),
  });

  const confirmedCount = (matches ?? []).filter((match) => match.confirmed).length;
  const yearsValid = /^\d{4}$/.test(electionYear.trim()) && /^\d{4}$/.test(sessionYear.trim());

  return (
    <AdminModal
      isOpen={isOpen}
      title="Reconcile Election Results"
      onClose={() => { reset(); onClose(); }}
      footer={
        matches === null ? (
          <>
            <Button variant="secondary" size="sm" onClick={() => { reset(); onClose(); }}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={!yearsValid} loading={findMutation.isLoading}
              onClick={() => findMutation.mutate()}>
              Find Matches
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={() => setMatches(null)}>Back</Button>
            <Button variant="primary" size="sm" disabled={confirmedCount === 0}
              loading={confirmMutation.isLoading} onClick={() => confirmMutation.mutate()}>
              Mark {confirmedCount} Elected
            </Button>
          </>
        )
      }
    >
      {matches === null ? (
        <div className="space-y-3">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Finds active legislative candidates from the election cycle whose person appears in the
            new session's legislator roster. Statewide executive offices are excluded — update those
            from the person's Roles tab.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="reconcile-election-year" className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                Election Year
              </label>
              <select id="reconcile-election-year" value={electionYear}
                onChange={(event) => {
                  const value = event.target.value;
                  setElectionYear(value);
                  const year = parseInt(value, 10);
                  if (Number.isFinite(year)) setSessionYear(String(year + 1));
                }}
                className={inputClasses}>
                <option value="">Select a cycle…</option>
                {cycleYears.map((year) => (
                  <option key={year} value={String(year)}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="reconcile-session-year" className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                Session Year
              </label>
              <input id="reconcile-session-year" type="number" min={2020} max={2036}
                placeholder="e.g. 2027" value={sessionYear}
                onChange={(event) => setSessionYear(event.target.value)} className={inputClasses} />
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {matches.length === 0 ? (
            <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
              No unreconciled matches for this cycle — either nothing to do, or the session roster
              hasn't been imported yet.
            </p>
          ) : (
            <>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                Confirming marks each candidacy <strong>elected</strong> and opens the officeholder
                term for the seat (the previous holder's term is ended).
              </p>
              <ul className="divide-y divide-border-light dark:divide-border-dark">
                {matches.map((match, index) => (
                  <li key={match.candidate_id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                    <input
                      id={`reconcile-match-${match.candidate_id}`}
                      type="checkbox"
                      checked={match.confirmed}
                      onChange={(event) =>
                        setMatches((current) =>
                          (current ?? []).map((row, rowIndex) =>
                            rowIndex === index ? { ...row, confirmed: event.target.checked } : row
                          )
                        )
                      }
                    />
                    <label htmlFor={`reconcile-match-${match.candidate_id}`} className="flex-1 cursor-pointer">
                      <span className="font-medium text-text-primary dark:text-text-dark-primary">
                        {match.full_name}
                      </span>{' '}
                      <span className="text-text-secondary dark:text-text-dark-secondary">
                        {match.office_sought} {match.district_display ?? match.district ?? ''}
                        {match.party_affiliation ? ` · ${match.party_affiliation}` : ''}
                        {' → '}
                        {match.chamber} District {padDistrictNumber(match.legislator_district) || '—'}, {match.session_year} session
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </AdminModal>
  );
};

export default ReconcileModal;
